// data validated in AEM
import { createMediaQuery } from '@oneaudi/aoa-shared-editorial-components';
import { Headline } from '@oneaudi/unified-web-components';

import styled from 'styled-components';

const alignmentMappings = {
  horizontal: {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  },
  vertical: {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  },
};

export const Container = styled.div`
  ${
    process.env.IS_LEGACY
      ? (props) => `background-color: ${props.theme.sys.color.canvas.default}`
      : ''
  };
  border-radius: ${process.env.IS_LEGACY ? '0' : '20px'};
  display: flex;
  flex: 1;
  height: 0;
  ${({ mobileBreakpointSizes }) => `padding-top: ${mobileBreakpointSizes.s}%;`}
  overflow: hidden;
  position: relative;

  ${createMediaQuery({
    m: ({ mobileBreakpointSizes }) => ({ 'padding-top': `${mobileBreakpointSizes.m}%` }),
    l: ({ size, index, layoutName, useNewAspectRatioBehavior }) => {
      if (useNewAspectRatioBehavior) {
        if (index === 0 && layoutName === 'Irregular - 2x1 split column right') {
          return { 'padding-top': `calc(${size}% + 12px)` };
        }
        if (index === 0 && layoutName === 'Irregular - 2x1 split column left') {
          return { 'padding-top': `calc(${size}% + 12px)` };
        }
      }

      if (index === 2 && layoutName === 'Irregular - 2x1 split column right') {
        return { 'padding-top': `calc(${size}% + 12px)` };
      }
      if (index === 2 && layoutName === 'Irregular - 2x1 split column left') {
        return { 'padding-top': `calc(${size}% + 12px)` };
      }
      if (layoutName === 'Irregular - 2x1 wide' && !useNewAspectRatioBehavior) {
        return { 'padding-top': `${size}vw` };
      }
      return size === null ? { height: '100%', 'padding-top': 0 } : { 'padding-top': `${size}%` };
    },
  })}

  &:hover video::-webkit-media-controls,
  &:hover video::-moz-media-controls,
  &:hover video::media-controls {
    ${process.env.IS_LEGACY ? 'transform: none !important;' : ''}
  }

  svg {
    pointer-events: none;
  }
`;

export const StyledAnchor = styled.a`
  display: block;
  text-decoration: none;
`;

export const InnerWrapper = styled.div`
  bottom: 0;
  display: flex;
  height: 100%;
  left: 0;
  ${({ horizontalAlignment, verticalAlignment }) =>
    createMediaQuery({
      xs: {
        'align-items': alignmentMappings.vertical[verticalAlignment],
        'justify-content': alignmentMappings.horizontal[horizontalAlignment],
      },
      l: {
        'align-items': alignmentMappings.vertical[verticalAlignment],
        'justify-content': alignmentMappings.horizontal[horizontalAlignment],
      },
    })}
  position: absolute;
  pointer-events: ${({ $isVideo }) => ($isVideo ? 'none' : 'auto')};
  right: 0;
  top: 0;
  width: 100%;
`;

export const MediaContainer = styled.div`
  height: 100%;
  overflow: hidden;
  position: absolute;
  pointer-events: auto;
  transition: all 2000ms ${(p) => p.theme.easing};
  width: 100%;
`;

export const ContentContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  justify-content: flex-start;
  padding: ${({ removePadding }) => (removePadding ? '0' : '28px')};
  pointer-events: ${({ $clickable, $isVideo }) => ($clickable && !$isVideo ? 'auto' : 'none')};
  position: relative;

  width: 100%;

  a,
  button,
  [role='button'],
  [role='link'] {
    pointer-events: auto;
  }
`;

export const NoAnchorContainer = styled.div``;

export const LogoContainer = styled.div`
  text-align: ${({ horizontalAlignment }) => horizontalAlignment};
`;

export const Logo = styled.img`
  margin-bottom: 12px;
  width: 110px;
`;

const iconSizes = {
  xs: '24px',
  l: '48px',
};

export const Icon = styled.div`
  color: white;
  margin: ${({ horizontalAlignment }) =>
    horizontalAlignment === 'left' || horizontalAlignment === 'right' ? '0' : '0 auto'};

  ${createMediaQuery({
    m: {
      margin: ({ horizontalAlignment }) =>
        horizontalAlignment === 'left' || horizontalAlignment === 'right' ? '0' : '0 auto',
    },
  })}

  margin-bottom: 12px;

  ${createMediaQuery({
    width: iconSizes,
  })};

  > svg {
    display: block;
    ${createMediaQuery({
      height: iconSizes,
      width: iconSizes,
    })};
  }
`;

export const Heading = styled.div`
  text-align: ${({ horizontalAlignment }) => horizontalAlignment};
`;

export const StyledHeadline = styled(Headline)`
  color: ${({ darkHeadingsEnabled }) => (darkHeadingsEnabled ? 'rgb(0, 0, 0)' : 'default')};
`;

export const SubHeading = styled.div`
  margin: 0;
  margin-top: 12px;
  text-align: ${({ horizontalAlignment }) => horizontalAlignment};
`;

export const StyledSubHeadline = styled(Headline)`
  color: ${({ darkHeadingsEnabled }) => (darkHeadingsEnabled ? 'rgb(0, 0, 0)' : 'default')};
`;

export const CTAsContainer = styled.div`
  margin-top: 28px;
  text-align: ${({ horizontalAlignment }) => horizontalAlignment};
`;

export const DisclaimerWrapper = styled.span`
  sup a {
    text-decoration: none;
  }
`;

export const Img = styled.img`
  display: block;
  max-width: 100%;
  width: 100%;

  /* Important for Chrome */
  backface-visibility: hidden;
  overflow-clip-margin: unset;
  transform: translateZ(0);
`;
